import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";

/* Same tokens as the detection screen so the first thing a user sees is
   already on-brand. */
const T = {
  paper: "#FAF8F3",
  paperDeep: "#F2EEE4",
  ink: "#14180F",
  ink80: "#3A4034",
  ink60: "#5A6151",
  ink40: "#6E7565",
  rule: "#DED8C9",
  forest: "#13301D",
  clay: "#A8482A",
  display: "'Fraunces', Georgia, serif",
  mono: "'JetBrains Mono', ui-monospace, monospace",
};

const TERMS = [
  "The AgriHyphen AI system is based on limited data and resources, which may result in inaccurate outcomes in certain cases.",
  "Recommendations provided must be validated and applied under the supervision of competent agricultural professionals.",
  "This tool is designed to complement, not replace, the expertise of an agronomist or agricultural expert.",
  "If the recommendations provided in the application seem unclear or difficult to apply, it is essential to contact an agronomist or agricultural expert for tailored advice.",
  "For critical decisions regarding your crops, we strongly recommend consulting with a local agronomist or agricultural expert.",
];

const PolicyDialog = ({ open, onAccept }) => {
  const { t } = useTranslation();

  const handleAccept = () => {
    localStorage.setItem("policyAccepted", "true");
    onAccept();
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 0, backgroundColor: T.paper },
      }}
    >
      <DialogTitle sx={{ px: { xs: 3, sm: 5 }, pt: { xs: 4, sm: 5 }, pb: 0 }}>
        <Typography
          component="span"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            fontFamily: T.mono,
            fontSize: "0.6875rem",
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: "0.14em",
            color: T.ink60,
            "&::before": {
              content: '""',
              display: "block",
              width: "24px",
              height: "1px",
              backgroundColor: T.clay,
            },
          }}
        >
          {t("Before you start")}
        </Typography>
        <Typography
          component="h2"
          sx={{
            mt: 2.5,
            fontFamily: T.display,
            fontWeight: 600,
            fontSize: { xs: "1.625rem", sm: "2rem" },
            lineHeight: 1.1,
            letterSpacing: "-0.026em",
            color: T.ink,
          }}
        >
          {t("Disclaimer and Terms of Use")}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 3, sm: 5 }, pt: 4, pb: 2 }}>
        {/* The one warning that must not be missed */}
        <Box
          sx={{
            borderLeft: `2px solid ${T.clay}`,
            pl: 2.5,
            py: 0.5,
            mb: 4,
          }}
        >
          <Typography
            sx={{
              fontFamily: T.display,
              fontSize: "1.0625rem",
              fontWeight: 600,
              lineHeight: 1.5,
              color: T.ink,
            }}
          >
            {t("non_plant_warning")}
          </Typography>
        </Box>

        <Typography
          sx={{ fontSize: "0.9375rem", lineHeight: 1.7, color: T.ink80 }}
        >
          {t(
            "This system uses AgriHyphen AI to provide support for plant disease detection. However, it does not replace the advice of a qualified agronomist."
          )}
        </Typography>

        <Typography
          sx={{
            mt: 4,
            fontFamily: T.mono,
            fontSize: "0.625rem",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            color: T.ink40,
          }}
        >
          {t("By using this application, you acknowledge and agree that:")}
        </Typography>

        <Box component="ol" sx={{ listStyle: "none", p: 0, mt: 1 }}>
          {TERMS.map((term, i) => (
            <Box
              component="li"
              key={term}
              sx={{
                display: "grid",
                gridTemplateColumns: "2rem 1fr",
                gap: "8px",
                py: 1.75,
                borderTop: `1px solid ${T.rule}`,
              }}
            >
              <Typography
                component="span"
                sx={{
                  pt: "3px",
                  fontFamily: T.mono,
                  fontSize: "0.6875rem",
                  fontWeight: 500,
                  color: T.clay,
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </Typography>
              <Typography
                sx={{ fontSize: "0.9375rem", lineHeight: 1.65, color: T.ink80 }}
              >
                {t(term)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            mt: 4,
            p: 2.5,
            backgroundColor: T.paperDeep,
            border: `1px solid ${T.rule}`,
          }}
        >
          <Typography
            sx={{ fontSize: "0.875rem", lineHeight: 1.65, color: T.ink60 }}
          >
            {t(
              "Photos uploaded through this application may be used for research purposes and to improve the performance of future AgriHyphen AI models for the common good."
            )}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 3, sm: 5 },
          py: 3,
          borderTop: `1px solid ${T.rule}`,
          backgroundColor: T.paper,
        }}
      >
        <Button
          onClick={handleAccept}
          variant="contained"
          fullWidth
          sx={{
            minHeight: "48px",
            borderRadius: 0,
            boxShadow: "none",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.9375rem",
            backgroundColor: T.ink,
            color: T.paper,
            "&:hover": { backgroundColor: T.forest, boxShadow: "none" },
          }}
        >
          {t("I Understand and Accept")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PolicyDialog;
